import React, { useState } from 'react'
import {
  Button,
  NotificationLevel,
  notify,
  spacing,
  Text,
} from '@retailer-platform/shared-components'
import styled from '@emotion/styled'
import { Divider } from '@retailer-platform/shared-components/src/tds'
import faker from 'faker'
import {
  Input,
  PropertyTitle,
  SectionHeader,
} from '../../shared-styled-components/SharedStyledComponents'
import { useDescribeSnowflakeTableLazyQuery } from '../../../api'
import { type FormProps } from './StepAttributeForm'
import ColumnMappingTable from './publish-to-snowflake-components/ColumnMappingTable'

const Container = styled.div({
  padding: `${spacing.X16} ${spacing.X20}`,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.X16,
})

const Row = styled.div({
  display: 'flex',
  flexDirection: 'row',
  gap: spacing.X16,
  alignItems: 'center',
})

const FlexOne = styled.div({
  flex: 1,
})

const OutputRow = styled.div({
  display: 'flex',
  flexDirection: 'row',
  gap: spacing.X16,
  alignItems: 'center',
})

export interface SnowflakeColumn {
  name: string
  type: string
  nonNull: boolean
  isPrimaryKey: boolean
  defaultValue: string | null
  campField: string | null
}

const PublishToSnowflakeForm: React.FC<FormProps> = ({ attributes, mode, onChange, version }) => {
  const [describeSnowflakeTable] = useDescribeSnowflakeTableLazyQuery({
    onError: () => {
      notify({
        level: NotificationLevel.Error,
        contents: 'Error loading snowflake table description',
      })
    },
    onCompleted: columnDefinitionResults => {
      const newMappings =
        columnDefinitionResults?.instacart_catalog_catflow_v1_CatflowAPI_DescribeSnowflakeTable?.columns?.map(
          (column: any) => ({
            name: column.name,
            type: column.type,
            nonNull: !column.nullable,
            isPrimaryKey: column.primaryKey,
            defaultValue: column.default,
            campField:
              attributes.columnMappings?.find((mapping: any) => mapping.name === column.name)
                ?.campField || '',
          })
        )
      onChange({ ...attributes, columnMappings: newMappings || [] })
    },
  })
  const snowflakeData = attributes.columnMappings?.length ? attributes.columnMappings : []
  const [newTableName, setNewTableName] = useState(attributes.snowflakeTable)

  return (
    <>
      <SectionHeader>Publish to Snowflake</SectionHeader>
      <Container>
        <Row>
          <PropertyTitle>Fully qualified table name:</PropertyTitle>
          {mode !== 'edit' ? (
            attributes.snowflakeTable
          ) : (
            <>
              <FlexOne>
                <Input
                  style={{ width: '100%' }}
                  value={newTableName}
                  onChange={(e: any) => setNewTableName(e.target.value)}
                  error={!newTableName}
                />
              </FlexOne>
              <Button
                variant="secondary"
                onClick={() => setNewTableName(attributes.snowflakeTable)}
                disabled={!attributes.snowflakeTable || newTableName === attributes.snowflakeTable}
              >
                Reset
              </Button>
              <Button
                variant={attributes.snowflakeTable === newTableName ? 'secondary' : 'primary'}
                disabled={!newTableName}
                onClick={() => {
                  describeSnowflakeTable({
                    variables: {
                      clientId: 'catalog_camp_ipp',
                      idempotencyKey: faker.random.uuid(),
                      tableName: newTableName,
                    },
                  })
                  onChange({ ...attributes, snowflakeTable: newTableName })
                }}
              >
                {attributes.snowflakeTable === newTableName ? 'Update' : 'Load table'}
              </Button>
            </>
          )}
        </Row>
        <Row>
          <PropertyTitle>Batch size:</PropertyTitle>
          <Input
            type="number"
            disabled={mode !== 'edit'}
            value={attributes.snowflakeBatchSize}
            onChange={(e: any) =>
              onChange({
                ...attributes,
                snowflakeBatchSize: Math.min(5000, Math.max(100, e.target.value)),
              })
            }
          />
        </Row>
        {attributes.snowflakeTable && (
          <>
            <Divider />
            <SectionHeader>Column Mapping</SectionHeader>
            <Container>
              <Text size="small">
                Select which camp fields will be mapped to which columns in your snowflake table.
                Mappings are required for columns that are &quot;not null&quot;, do not have a
                default value, and are not a primary key.
              </Text>
              <Text size="small" weight="semibold">
                Note: Inserts are done in batches. if any single rows has bad data (e.g. trying to
                insert a string into a number column), the entire batch will fail.
              </Text>
            </Container>
            <ColumnMappingTable
              columns={snowflakeData}
              onChange={columns => onChange({ ...attributes, columnMappings: columns })}
              disabled={mode !== 'edit' || !(newTableName === attributes.snowflakeTable)}
            />
            <Divider />
            <SectionHeader>Output Fields</SectionHeader>
            <Container>
              <OutputRow>
                <Text weight="semibold" size="small">
                  {attributes.snowflakeTable}_insert_successful:
                </Text>
                <Text size="small">A boolean that if the task was successfully inserted.</Text>
              </OutputRow>
              <OutputRow>
                <Text weight="semibold" size="small">
                  {attributes.snowflakeTable}_insert_error_message:
                </Text>
                <Text size="small">
                  A string that contains the error message if the task was not successfully
                  inserted.
                </Text>
              </OutputRow>
            </Container>
          </>
        )}
      </Container>
    </>
  )
}

export default PublishToSnowflakeForm
